import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type Row,
  type RowSelectionState,
  type Table as TableType,
} from '@tanstack/react-table';
import { cn } from '@workspace/ui/lib/utils';
import { useEffect, useState, type ReactNode } from 'react';
import ReactPaginate from 'react-paginate';
import { Icon } from '../icon/icon';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '@workspace/ui/components/context-menu';

interface TableProps<TData, TValue> {
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  select?: 'single' | 'multiple';
  onSelect?: (row: RowSelectionState) => void;
  onTableLoad?: (table: TableType<TData>) => void;
  onRowClick?: (row: Row<TData>) => void;
  onPageChange?: (page: number) => void;
  paginate?: boolean;
  emptyContent?: ReactNode;
  contextMenu?: (row: Row<TData>) => ReactNode;
  totalPageCount?: number;
  pageSize?: number;
  pageIndex?: number;
  getRowId?: (row: TData) => string;
}

const columnSizingHandler = (
  thElem: HTMLTableCellElement | null,
  table: TableType<any>,
  column: Column<any>,
) => {
  if (!thElem) return;
  // If you don't do that, there will be an infinite loop. We update the value in state only if the value has actually changed.
  if (table.getState().columnSizing[column.id] === thElem.getBoundingClientRect().width) return;

  table.setColumnSizing((prevSizes) => ({
    ...prevSizes,
    // 100% accurate float-point width, even if table content is loaded async
    [column.id]: thElem.getBoundingClientRect().width,
  }));
};

export function Table<TData, TValue>({
  data,
  columns,
  select,
  onSelect,
  onTableLoad,
  onRowClick,
  onPageChange,
  paginate,
  emptyContent,
  contextMenu,
  totalPageCount,
  pageSize,
  pageIndex,
  getRowId,
}: TableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: pageSize ?? 10,
      },
    },
    paginateExpandedRows: true,
    getRowCanExpand: (row) => true,
    state: {
      rowSelection,
    },
    enableRowSelection: select ? true : false,
    enableMultiRowSelection: select === 'multiple' ? true : false,
    enableSubRowSelection: true,
    onRowSelectionChange: select ? setRowSelection : undefined,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: paginate ? getPaginationRowModel() : undefined,
    getRowId,
  });

  useEffect(() => {
    onSelect?.(rowSelection);
  }, [rowSelection, onSelect]);

  useEffect(() => {
    if (!onTableLoad) return;

    onTableLoad(table);
  }, [onTableLoad, table]);

  return (
    <>
      <table className='w-full min-w-0'>
        <thead className='h-10 bg-surface-section'>
          <tr>
            {table.getHeaderGroups().map((group) => {
              return group.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className='px-4 text-left text-text-default text-size-6 font-bold leading-font-height-6'
                    ref={(thElement) => columnSizingHandler(thElement, table, header.column)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                );
              });
            })}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => {
              return (
                <ContextMenu key={row.id}>
                  <ContextMenuTrigger asChild>
                    <tr
                      onClick={() => {
                        onRowClick?.(row);
                        row.getToggleExpandedHandler();
                      }}
                      className={cn(
                        'h-12 shadow-[0px_-1px_0px_0px_#EDEDED_inset] hover:bg-surface-blue-tint',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td
                            key={cell.id}
                            className={cn(
                              'px-4 py-2 truncate text-text-default! text-size-6 font-regular leading-font-height-5 tracking-[-0.1px]',
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                  </ContextMenuTrigger>
                  {contextMenu && (
                    <ContextMenuContent className='border-border rounded-[16px]'>
                      {contextMenu(row)}
                    </ContextMenuContent>
                  )}
                </ContextMenu>
              );
            })
          ) : (
            <tr className='w-full'>
              <td colSpan={columns.length}>{emptyContent}</td>
            </tr>
          )}
        </tbody>
      </table>
      {paginate && (
        <ReactPaginate
          forcePage={pageIndex ?? 0}
          pageCount={totalPageCount ?? table.getPageCount()}
          previousLabel={<Icon name='ChevronLeft' size={16} color='var(--icon-icon-disabled)' />}
          nextLabel={<Icon name='ChevronRight' size={16} color='var(--icon-icon-disabled)' />}
          breakLabel={<Icon name='Meatball' size={24} color='var(--icon-icon-default' />}
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          className='w-full flex items-center justify-center mt-3 gap-4'
          pageClassName='relative min-w-[24px] h-[24px] text-size-6 font-regular leading-font-height-6 text-center rounded-[var(--radius-skeleton)] cursor-pointer [&>a]:flex [&>a]:w-full [&>a]:h-full [&>a]:items-center [&>a]:justify-center'
          activeClassName='bg-action-blue-tint-default text-text-blue'
          onPageChange={(page) => {
            onPageChange?.(page.selected);
          }}
        />
      )}
    </>
  );
}
